import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faBars,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function DogrulamaKodlari() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [otpCodes, setOtpCodes] = useState([]);
  const [filteredOtpCodes, setFilteredOtpCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [codesPerPage, setCodesPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchOtpCodes();
  }, []);

  // Filter OTP codes based on search input
  useEffect(() => {
    filterOtpCodes();
  }, [otpCodes, filters]);

  const filterOtpCodes = () => {
    let filtered = [...otpCodes];

    // Filter by phone number
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((otp) =>
        otp.phone?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredOtpCodes(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle change in number of codes per page
  const handleCodesPerPageChange = (e) => {
    setCodesPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters or codesPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, codesPerPage]);

  const fetchOtpCodes = async () => {
    try {
      setLoading(true);

      // Fetch OTP codes
      const { data, error } = await supabase
        .from('otps')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get unique phone numbers from OTP codes
      const phoneNumbers = [
        ...new Set(data.map((otp) => otp.phone).filter(Boolean)),
      ];

      // If there are phone numbers, fetch matching user profiles
      let userProfiles = [];
      if (phoneNumbers.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, phone_number')
          .in('phone_number', phoneNumbers);

        if (profilesError) {
          console.error('Error fetching user profiles:', profilesError);
        } else {
          userProfiles = profilesData || [];
        }
      }

      // Enrich OTP data with user information
      const enrichedData = data.map((otp) => {
        const matchingUser = userProfiles.find(
          (profile) => profile.phone_number === otp.phone
        );
        return {
          ...otp,
          user_info: matchingUser
            ? {
                first_name: matchingUser.first_name || '',
                last_name: matchingUser.last_name || '',
              }
            : null,
        };
      });

      setOtpCodes(enrichedData || []);
      setFilteredOtpCodes(enrichedData || []);
    } catch (error) {
      console.error('Error fetching OTP codes:', error);
      setError('Doğrulama kodlarını yüklerken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm', { locale: tr });
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateString;
    }
  };

  // Pagination logic
  const indexOfLastCode = currentPage * codesPerPage;
  const indexOfFirstCode = indexOfLastCode - codesPerPage;
  const currentCodes = filteredOtpCodes.slice(
    indexOfFirstCode,
    indexOfLastCode
  );
  const totalPages = Math.ceil(filteredOtpCodes.length / codesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Doğrulama Kodları
              </h1>
              <p className="text-sm text-gray-500">
                Sistem tarafından gönderilen tüm doğrulama kodlarının listesi
              </p>
            </div>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon Numarası Ara
                </label>
                <input
                  type="text"
                  name="search"
                  placeholder="Telefon Numarası"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-100 p-4 rounded text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="text-[#4263EB] text-3xl"
              />
              <span className="ml-2 text-gray-600">Yükleniyor...</span>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Telefon Numarası
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kullanıcı Adı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Doğrulama Kodu
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Oluşturulma Tarihi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentCodes.length > 0 ? (
                    currentCodes.map((otp) => (
                      <tr key={otp.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {otp.phone || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {otp.user_info
                              ? `${otp.user_info.first_name} ${otp.user_info.last_name}`.trim() ||
                                'N/A'
                              : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {otp.otp || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">
                            {formatDate(otp.created_at)}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-6 py-4 text-center text-sm text-gray-500"
                      >
                        Henüz doğrulama kodu bulunmuyor.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {filteredOtpCodes.length > 0 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Önceki
                      </button>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={codesPerPage}
                          onChange={handleCodesPerPageChange}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Sonraki
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700 mr-4">
                        Görüntülenen{' '}
                        <span className="font-medium">
                          {indexOfFirstCode + 1}
                        </span>{' '}
                        -{' '}
                        <span className="font-medium">
                          {Math.min(indexOfLastCode, filteredOtpCodes.length)}
                        </span>{' '}
                        /{' '}
                        <span className="font-medium">
                          {filteredOtpCodes.length}
                        </span>{' '}
                        sonuç
                      </p>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={codesPerPage}
                          onChange={handleCodesPerPageChange}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                            currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={faChevronLeft}
                            className="h-4 w-4"
                          />
                        </button>

                        {/* Page Numbers */}
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show current page, first page, last page, and one page before and after current
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 &&
                              pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === pageNumber
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          }
                          // Show dots if there's a gap
                          if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}

                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                            currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="h-4 w-4"
                          />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default DogrulamaKodlari;
